
import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface Abono {
  id: string;
  monto: number;
  metodo_pago: string;
  fecha_pago: string;
  banco?: string | null;
  numero_comprobante?: string | null;
  notas?: string | null;
  created_at: string;
}

interface Pago {
  id: string;
  usuario_id: string;
  monto: number;
  metodo_pago: string;
  concepto: string;
  fecha_pago: string;
  estado: string;
  sucursal_id: string;
  created_at: string;
  banco?: string;
  numero_comprobante?: string;
  notas?: string;
  plan_id?: string | null;
  monto_abonado?: number | null;
  vigencia_aplicada_at?: string | null;
  usuario?: {
    nombre: string;
    email: string;
    telefono: string;
    tipo_membresia: string;
  };
}

interface DetallePagoModalProps {
  pago: Pago;
  onClose: () => void;
  onPagoActualizado?: () => void;
  metodosPagoDisponibles?: Array<{ value: string; label: string }>;
}

export default function DetallePagoModal({
  pago,
  onClose,
  onPagoActualizado,
  metodosPagoDisponibles = [],
}: DetallePagoModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [abonos, setAbonos] = useState<Abono[]>([]);
  const [cargandoAbonos, setCargandoAbonos] = useState(false);
  const [guardandoAbono, setGuardandoAbono] = useState(false);
  const [errorAbono, setErrorAbono] = useState('');
  const fechaHoy = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
  const [nuevoAbono, setNuevoAbono] = useState({
    monto: '',
    metodo_pago: metodosPagoDisponibles[0]?.value || '',
    fecha_pago: fechaHoy,
    banco: '',
    numero_comprobante: '',
    notas: '',
  });

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  useEffect(() => {
    if (!pago.plan_id && pago.monto_abonado == null) return;
    let activo = true;
    setCargandoAbonos(true);
    supabase
      .from('pago_abonos')
      .select('id,monto,metodo_pago,fecha_pago,banco,numero_comprobante,notas,created_at')
      .eq('pago_id', pago.id)
      .order('fecha_pago', { ascending: true })
      .order('created_at', { ascending: true })
      .then(({ data, error }) => {
        if (!activo) return;
        if (error) setErrorAbono(error.message);
        else setAbonos((data || []) as Abono[]);
        setCargandoAbonos(false);
      });
    return () => { activo = false; };
  }, [pago.id, pago.plan_id, pago.monto_abonado]);

  useEffect(() => {
    if (!metodosPagoDisponibles.length) return;
    setNuevoAbono((prev) => metodosPagoDisponibles.some((metodo) => metodo.value === prev.metodo_pago)
      ? prev
      : { ...prev, metodo_pago: metodosPagoDisponibles[0].value });
  }, [metodosPagoDisponibles]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  const formatearMonto = (monto: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(monto);
  };

  const formatearFecha = (fecha: string) => {
    // La fecha de pago es una fecha comercial, no un instante que deba
    // convertirse desde UTC. Conservamos siempre su día calendario original.
    const fechaComercial = fecha.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
    const d = fechaComercial
      ? new Date(`${fechaComercial}T12:00:00`)
      : new Date(fecha);
    return d.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatearFechaHora = (fecha: string) => {
    return new Date(fecha).toLocaleString('es-CL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const obtenerColorEstado = (estado: string) => {
    switch (estado) {
      case 'completado':
        return 'bg-green-100 text-green-700 border border-green-200';
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
      case 'parcial':
        return 'bg-indigo-100 text-indigo-700 border border-indigo-200';
      case 'rechazado':
        return 'bg-red-100 text-red-700 border border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  };

  const obtenerIconoEstado = (estado: string) => {
    switch (estado) {
      case 'completado':
        return 'ri-checkbox-circle-fill text-green-500';
      case 'pendiente':
        return 'ri-time-fill text-yellow-500';
      case 'parcial':
        return 'ri-pie-chart-fill text-indigo-500';
      case 'rechazado':
        return 'ri-close-circle-fill text-red-500';
      default:
        return 'ri-question-fill text-gray-500';
    }
  };

  const obtenerIconoMetodo = (metodo: string) => {
    switch (metodo) {
      case 'efectivo':
        return 'ri-money-dollar-circle-line';
      case 'tarjeta':
        return 'ri-bank-card-line';
      case 'transferencia':
        return 'ri-exchange-line';
      case 'cheque':
        return 'ri-file-list-3-line';
      case 'deposito':
        return 'ri-bank-line';
      default:
        return 'ri-wallet-line';
    }
  };

  const obtenerNombreMetodo = (metodo: string) => {
    switch (metodo) {
      case 'efectivo': return 'Efectivo';
      case 'tarjeta': return 'Tarjeta';
      case 'transferencia': return 'Transferencia Bancaria';
      case 'cheque': return 'Cheque';
      case 'deposito': return 'Depósito';
      case 'mixto': return 'Varios medios';
      default: return metodo;
    }
  };

  const montoAbonado = pago.monto_abonado == null
    ? abonos.reduce((total, abono) => total + Number(abono.monto), 0)
    : Number(pago.monto_abonado);
  const saldoPendiente = Math.max(0, Number(pago.monto) - montoAbonado);
  const admiteAbonos = Boolean(pago.plan_id) && pago.estado === 'parcial' && !pago.vigencia_aplicada_at;

  const registrarAbono = async () => {
    setErrorAbono('');
    const monto = Number(nuevoAbono.monto);
    if (!Number.isFinite(monto) || monto <= 0) {
      setErrorAbono('Ingresa un monto válido.');
      return;
    }
    if (monto > saldoPendiente) {
      setErrorAbono(`El abono supera el saldo pendiente de ${formatearMonto(saldoPendiente)}.`);
      return;
    }
    if (!metodosPagoDisponibles.some((metodo) => metodo.value === nuevoAbono.metodo_pago)) {
      setErrorAbono('Selecciona un medio de pago habilitado.');
      return;
    }
    try {
      setGuardandoAbono(true);
      const { error } = await supabase.rpc('agregar_abono_pago_membresia_admin', {
        p_pago_id: pago.id,
        p_monto: monto,
        p_metodo_pago: nuevoAbono.metodo_pago,
        p_fecha_pago: nuevoAbono.fecha_pago,
        p_banco: nuevoAbono.metodo_pago === 'transferencia' ? nuevoAbono.banco || null : null,
        p_numero_comprobante: nuevoAbono.numero_comprobante || null,
        p_notas: nuevoAbono.notas || null,
      });
      if (error) throw error;
      onPagoActualizado?.();
    } catch (error) {
      setErrorAbono(error instanceof Error ? error.message : 'No se pudo registrar el abono.');
    } finally {
      setGuardandoAbono(false);
    }
  };

  const iniciales = pago.usuario?.nombre
    ? pago.usuario.nombre.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : '??';

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fadeIn"
      onClick={handleBackdropClick}
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <div
        ref={modalRef}
        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
        style={{ animation: 'slideUp 0.25s ease-out' }}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-teal-600 to-teal-500 px-6 py-5">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white text-lg"></i>
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <i className="ri-receipt-line text-2xl text-white"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Detalle del Pago</h2>
              <p className="text-teal-100 text-sm mt-0.5">ID: {pago.id.slice(0, 8).toUpperCase()}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 max-h-[65vh] overflow-y-auto">
          {/* Monto y Estado */}
          <div className="flex items-center justify-between mb-6 pb-5 border-b border-gray-100">
            <div>
              <p className="text-sm text-gray-500 mb-1">Monto total</p>
              <p className="text-3xl font-bold text-gray-900">{formatearMonto(Number(pago.monto))}</p>
            </div>
            <div className="text-right flex flex-col items-end gap-2">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium capitalize ${obtenerColorEstado(pago.estado)}`}>
                <i className={`${obtenerIconoEstado(pago.estado)} text-base`}></i>
                {pago.estado}
              </span>
            </div>
          </div>

          {(pago.plan_id || pago.monto_abonado != null) && (
            <div className="mb-6 grid grid-cols-3 gap-2 rounded-xl bg-indigo-50 p-3 text-center">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Total</p>
                <p className="mt-1 text-sm font-bold text-gray-900">{formatearMonto(Number(pago.monto))}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Abonado</p>
                <p className="mt-1 text-sm font-bold text-indigo-700">{formatearMonto(montoAbonado)}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Saldo</p>
                <p className={`mt-1 text-sm font-bold ${saldoPendiente === 0 ? 'text-green-700' : 'text-amber-700'}`}>
                  {formatearMonto(saldoPendiente)}
                </p>
              </div>
            </div>
          )}

          {/* Info del Usuario */}
          <div className="mb-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Cliente</p>
            <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4">
              <div className="w-11 h-11 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-teal-700">{iniciales}</span>
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 text-sm">{pago.usuario?.nombre || 'Usuario desconocido'}</p>
                <p className="text-xs text-gray-500 truncate">{pago.usuario?.email || '—'}</p>
                {pago.usuario?.telefono && (
                  <p className="text-xs text-gray-500">{pago.usuario.telefono}</p>
                )}
              </div>
              {pago.usuario?.tipo_membresia && (
                <span className="ml-auto px-2.5 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-full capitalize whitespace-nowrap">
                  {pago.usuario.tipo_membresia}
                </span>
              )}
            </div>
          </div>

          {/* Detalles del Pago */}
          <div className="mb-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Información del Pago</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-xl p-3.5">
                <p className="text-xs text-gray-500 mb-1">Concepto</p>
                <p className="text-sm font-medium text-gray-900">{pago.concepto || '—'}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3.5">
                <p className="text-xs text-gray-500 mb-1">Método de Pago</p>
                <div className="flex items-center gap-1.5">
                  <i className={`${obtenerIconoMetodo(pago.metodo_pago)} text-gray-600`}></i>
                  <p className="text-sm font-medium text-gray-900">{obtenerNombreMetodo(pago.metodo_pago)}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3.5">
                <p className="text-xs text-gray-500 mb-1">Fecha de Pago</p>
                <p className="text-sm font-medium text-gray-900">{formatearFecha(pago.fecha_pago)}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3.5">
                <p className="text-xs text-gray-500 mb-1">Registrado</p>
                <p className="text-sm font-medium text-gray-900">{formatearFechaHora(pago.created_at)}</p>
              </div>
              {pago.banco && (
                <div className="bg-gray-50 rounded-xl p-3.5">
                  <p className="text-xs text-gray-500 mb-1">Banco</p>
                  <p className="text-sm font-medium text-gray-900">{pago.banco}</p>
                </div>
              )}
              {pago.numero_comprobante && (
                <div className="bg-gray-50 rounded-xl p-3.5">
                  <p className="text-xs text-gray-500 mb-1">N° Comprobante</p>
                  <p className="text-sm font-medium text-gray-900">{pago.numero_comprobante}</p>
                </div>
              )}
            </div>
          </div>

          {(pago.plan_id || pago.monto_abonado != null) && (
            <div className="mb-5">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Vouchers y abonos</p>
              {cargandoAbonos ? (
                <div className="rounded-xl bg-gray-50 p-4 text-center text-sm text-gray-500">
                  <i className="ri-loader-4-line mr-2 animate-spin"></i>Cargando comprobantes...
                </div>
              ) : abonos.length === 0 ? (
                <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-500">No hay comprobantes asociados.</div>
              ) : (
                <div className="space-y-2">
                  {abonos.map((abono, index) => (
                    <div key={abono.id} className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Abono {index + 1} · {obtenerNombreMetodo(abono.metodo_pago)}</p>
                          <p className="mt-1 text-xs text-gray-500">{formatearFecha(abono.fecha_pago)}</p>
                        </div>
                        <p className="text-sm font-bold text-gray-900">{formatearMonto(Number(abono.monto))}</p>
                      </div>
                      {(abono.numero_comprobante || abono.banco || abono.notas) && (
                        <div className="mt-2 border-t border-gray-200 pt-2 text-xs text-gray-600">
                          {abono.numero_comprobante && <p>Comprobante: {abono.numero_comprobante}</p>}
                          {abono.banco && <p>Banco: {abono.banco}</p>}
                          {abono.notas && <p>{abono.notas}</p>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {admiteAbonos && (
            <div className="mb-5 rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
              <div className="mb-3">
                <p className="text-sm font-semibold text-indigo-950">Agregar abono</p>
                <p className="text-xs text-indigo-800">La vigencia se aplicará recién al completar {formatearMonto(saldoPendiente)}.</p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="text-xs font-semibold text-gray-600">
                  Monto
                  <input
                    type="number"
                    min="1"
                    max={saldoPendiente}
                    value={nuevoAbono.monto}
                    onChange={(event) => setNuevoAbono((prev) => ({ ...prev, monto: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"
                  />
                </label>
                <label className="text-xs font-semibold text-gray-600">
                  Medio de pago
                  <select
                    value={nuevoAbono.metodo_pago}
                    onChange={(event) => setNuevoAbono((prev) => ({ ...prev, metodo_pago: event.target.value, banco: '' }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"
                  >
                    {metodosPagoDisponibles.map((metodo) => (
                      <option key={metodo.value} value={metodo.value}>{metodo.label}</option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold text-gray-600">
                  N° de comprobante
                  <input
                    type="text"
                    value={nuevoAbono.numero_comprobante}
                    onChange={(event) => setNuevoAbono((prev) => ({ ...prev, numero_comprobante: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"
                  />
                </label>
                <label className="text-xs font-semibold text-gray-600">
                  Fecha
                  <input
                    type="date"
                    value={nuevoAbono.fecha_pago}
                    onChange={(event) => setNuevoAbono((prev) => ({ ...prev, fecha_pago: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"
                  />
                </label>
                {nuevoAbono.metodo_pago === 'transferencia' && (
                  <label className="text-xs font-semibold text-gray-600 sm:col-span-2">
                    Banco
                    <input
                      type="text"
                      value={nuevoAbono.banco}
                      onChange={(event) => setNuevoAbono((prev) => ({ ...prev, banco: event.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"
                    />
                  </label>
                )}
                <label className="text-xs font-semibold text-gray-600 sm:col-span-2">
                  Nota opcional
                  <input
                    type="text"
                    value={nuevoAbono.notas}
                    onChange={(event) => setNuevoAbono((prev) => ({ ...prev, notas: event.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"
                  />
                </label>
              </div>
              {errorAbono && <p className="mt-3 text-xs font-medium text-red-600">{errorAbono}</p>}
              <button
                type="button"
                onClick={registrarAbono}
                disabled={guardandoAbono || metodosPagoDisponibles.length === 0}
                className="mt-4 w-full rounded-lg bg-indigo-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-800 disabled:opacity-50"
              >
                {guardandoAbono ? <><i className="ri-loader-4-line mr-2 animate-spin"></i>Guardando...</> : 'Registrar abono'}
              </button>
            </div>
          )}

          {/* Notas */}
          {pago.notas && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Notas</p>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-700 leading-relaxed">{pago.notas}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
